import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

function formatComplianceDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function statusBadgeVariant(status) {
  if (status === 'expired') {
    return {
      className: 'border-red-500/50 bg-red-500/15 text-red-400',
    };
  }
  if (status === 'expiring_critical') {
    return {
      className: 'border-orange-500/50 bg-orange-500/15 text-orange-400',
    };
  }
  if (status === 'expiring_soon') {
    return {
      className: 'border-amber-500/50 bg-amber-500/15 text-amber-400',
    };
  }
  if (status === 'ok' || status === 'valid') {
    return {
      className: 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400',
    };
  }
  return {
    className: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
  };
}

function formatComplianceDateRangeDisplay(startStr, endStr, t) {
  const startLabel = formatComplianceDate(startStr);
  const endLabel = formatComplianceDate(endStr);
  if (startLabel && endLabel) {
    return `${t('complianceStartDate')}: ${startLabel} — ${t('complianceEndDate')}: ${endLabel}`;
  }
  if (endLabel) {
    return `${t('complianceEndDate')}: ${endLabel}`;
  }
  if (startLabel) {
    return `${t('complianceStartDate')}: ${startLabel}`;
  }
  return null;
}

function ComplianceExpiryCell({
  fieldKey,
  row,
  t,
  statusLabelFor,
}) {
  const rtoApplicable = row.rto_applicable;
  const isRtoField = fieldKey === 'rc_expiry' || fieldKey === 'puc_expiry';

  if (isRtoField && rtoApplicable === false) {
    return <span className="text-slate-400">{t('complianceNa')}</span>;
  }

  const field = row.compliance?.[fieldKey];
  const dateVal = field?.date ?? null;
  const startVal = field?.startDate ?? null;
  const status = field?.status ?? 'not_set';

  if (!dateVal && !startVal) {
    return <span className="text-slate-400">{t('complianceNotSet')}</span>;
  }

  const rangeText = formatComplianceDateRangeDisplay(startVal, dateVal, t);
  if (!dateVal) {
    return <span className="text-sm text-slate-200">{rangeText}</span>;
  }

  const badge = statusBadgeVariant(status);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-slate-200">{rangeText || formatComplianceDate(dateVal)}</span>
      <Badge className={badge.className}>{statusLabelFor(status)}</Badge>
    </div>
  );
}

export default function MachineCompliancePanel({ authToken }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const statusLabelFor = useCallback((status) => {
    if (status === 'expired') return t('complianceExpired');
    if (status === 'expiring_critical') return t('complianceExpiringCritical');
    if (status === 'expiring_soon') return t('complianceExpiringSoon');
    if (status === 'ok' || status === 'valid') return t('complianceValid');
    return t('complianceNotSet');
  }, [t]);

  const load = useCallback(async () => {
    if (!authToken) {
      setItems([]);
      setError(t('failedToLoad'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/machines/compliance-alerts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      setError(err.message || t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card className="mb-6 w-full min-w-0 rounded-2xl border-de-gold/30 bg-gradient-to-br from-[#122a4a]/60 via-[#0a1628]/95 to-[#060e1c] de-glow-gold shadow-2xl">
      <CardHeader className="p-4 pb-2 sm:p-5">
        <CardTitle className="text-base font-semibold text-[#C9A84C]">
          {String.fromCodePoint(0x1F4CB)} {t('complianceAlertsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-3 p-4 pt-0 sm:p-5 sm:pt-0">
        {loading && (
          <p className="text-sm text-slate-400">{t('loading')}</p>
        )}

        {!loading && error && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-400">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              className="border-de-gold/40 text-de-gold hover:bg-de-gold/10"
            >
              {t('retryLabel')}
            </Button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-emerald-400">{t('complianceAllCompliant')}</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    t('complianceColMachine'),
                    t('complianceColOwner'),
                    t('complianceColInsurance'),
                    t('complianceColRcBook'),
                    t('complianceColPuc'),
                    'Action',
                  ].map((h) => (
                    <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Badge>{row.machine_id}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-300">
                      {row.ownerName || row.owner_id}
                    </TableCell>
                    <TableCell>
                      <ComplianceExpiryCell
                        fieldKey="insurance_expiry"
                        row={row}
                        t={t}
                        statusLabelFor={statusLabelFor}
                      />
                    </TableCell>
                    <TableCell>
                      <ComplianceExpiryCell
                        fieldKey="rc_expiry"
                        row={row}
                        t={t}
                        statusLabelFor={statusLabelFor}
                      />
                    </TableCell>
                    <TableCell>
                      <ComplianceExpiryCell
                        fieldKey="puc_expiry"
                        row={row}
                        t={t}
                        statusLabelFor={statusLabelFor}
                      />
                    </TableCell>
                    <TableCell className="text-slate-500">—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
